/**
 * Report date range — the one selector every report shares (roadmap RP-7).
 *
 * A range is either a rolling window measured from now, a custom pair of
 * days, or a cycle. It lives in the URL (`range`, `from`, `to`, `cycle`) so a
 * report address says exactly what it shows, and it prints as a PQL clause so
 * a drill-through from any chart lands on the item list with the same window.
 *
 * Windows are measured on the item's created date. That is the one date every
 * item has; a report that keys on another date says so in its own copy.
 */

import { toDate } from '../../../../lib/dateValue';

export type RangeKey = 'all' | '30d' | '90d' | '1y' | 'custom' | 'cycle';

export interface ReportRange {
  key: RangeKey;
  /** ISO day (YYYY-MM-DD) — custom only. */
  from?: string;
  /** ISO day (YYYY-MM-DD) — custom only. */
  to?: string;
  /** Cycle id — cycle only. */
  cycleId?: string;
}

export interface RangeCycle {
  id: string;
  name: string;
  createdAt: Date | string;
  startedAt?: Date | string | null;
  completedAt?: Date | string | null;
}

export interface ResolvedRange {
  key: RangeKey;
  from: Date | null;
  to: Date | null;
  cycleId: string | null;
  /** What the person reads next to the control: "Last 30 days", "Cycle: Q4". */
  label: string;
  /** PQL clause for the item list, or null when the range is everything. */
  pql: string | null;
}

export const RANGE_OPTIONS: ReadonlyArray<{ key: RangeKey; label: string }> = [
  { key: 'all', label: 'All time' },
  { key: '30d', label: 'Last 30 days' },
  { key: '90d', label: 'Last quarter' },
  { key: '1y', label: 'Last year' },
  { key: 'cycle', label: 'A cycle' },
  { key: 'custom', label: 'Custom' },
];

export const DEFAULT_RANGE: ReportRange = { key: 'all' };

const DAY_MS = 24 * 60 * 60 * 1000;
const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function endOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
}

function isoDay(s: string | undefined): Date | null {
  if (!s || !ISO_DAY.test(s)) return null;
  const [y, m, d] = s.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Read a range out of URL search params. Unknown or partial values fall back to "all". */
export function rangeFromParams(params: URLSearchParams): ReportRange {
  const key = params.get('range') as RangeKey | null;
  if (!key || !RANGE_OPTIONS.some((o) => o.key === key)) return DEFAULT_RANGE;
  if (key === 'custom') {
    const from = params.get('from') ?? undefined;
    const to = params.get('to') ?? undefined;
    return { key, from: from && ISO_DAY.test(from) ? from : undefined, to: to && ISO_DAY.test(to) ? to : undefined };
  }
  if (key === 'cycle') {
    const cycleId = params.get('cycle') ?? undefined;
    return cycleId ? { key, cycleId } : DEFAULT_RANGE;
  }
  return { key };
}

/** Write a range into URL search params (mutates and returns the same object). */
export function rangeToParams(range: ReportRange, params: URLSearchParams): URLSearchParams {
  params.delete('range'); params.delete('from'); params.delete('to'); params.delete('cycle');
  if (range.key === 'all') return params;
  params.set('range', range.key);
  if (range.key === 'custom') {
    if (range.from) params.set('from', range.from);
    if (range.to) params.set('to', range.to);
  }
  if (range.key === 'cycle' && range.cycleId) params.set('cycle', range.cycleId);
  return params;
}

/**
 * Turn a range into concrete bounds, a label, and the matching PQL clause.
 * `now` is injectable so tests are deterministic.
 */
export function resolveRange(range: ReportRange, cycles: ReadonlyArray<RangeCycle> = [], now: Date = new Date()): ResolvedRange {
  switch (range.key) {
    case '30d':
      return { key: range.key, from: new Date(now.getTime() - 30 * DAY_MS), to: null, cycleId: null, label: 'Last 30 days', pql: 'created >= -30d' };
    case '90d':
      return { key: range.key, from: new Date(now.getTime() - 90 * DAY_MS), to: null, cycleId: null, label: 'Last quarter', pql: 'created >= -90d' };
    case '1y':
      return { key: range.key, from: new Date(now.getTime() - 365 * DAY_MS), to: null, cycleId: null, label: 'Last year', pql: 'created >= -1y' };
    case 'custom': {
      const from = isoDay(range.from);
      const to = isoDay(range.to);
      if (!from && !to) return { key: 'all', from: null, to: null, cycleId: null, label: 'All time', pql: null };
      const label = from && to ? `${range.from} to ${range.to}` : from ? `From ${range.from}` : `Up to ${range.to}`;
      const pql = from && to
        ? (range.from === range.to ? `created = ${range.from}` : `created = ${range.from}..${range.to}`)
        : from ? `created >= ${range.from}` : `created <= ${range.to}`;
      return { key: 'custom', from: from ? startOfDay(from) : null, to: to ? endOfDay(to) : null, cycleId: null, label, pql };
    }
    case 'cycle': {
      const cycle = cycles.find((c) => c.id === range.cycleId);
      if (!cycle) return { key: 'all', from: null, to: null, cycleId: null, label: 'All time', pql: null };
      const from = toDate(cycle.startedAt) ?? toDate(cycle.createdAt);
      const to = toDate(cycle.completedAt);
      return { key: 'cycle', from, to, cycleId: cycle.id, label: `Cycle: ${cycle.name}`, pql: `cycle = "${cycle.id}"` };
    }
    case 'all':
    default:
      return { key: 'all', from: null, to: null, cycleId: null, label: 'All time', pql: null };
  }
}

/**
 * Does a row fall inside the range?
 *
 * A cycle range is membership, not dates: the row's `cycleIds` must include the
 * cycle. Every other range is a window on the row's created date.
 */
export function rowInRange(
  row: { createdAt?: Date | string | null; cycleIds?: ReadonlyArray<string> },
  resolved: ResolvedRange,
): boolean {
  if (resolved.key === 'all') return true;
  if (resolved.key === 'cycle') return !!resolved.cycleId && (row.cycleIds ?? []).includes(resolved.cycleId);
  const created = toDate(row.createdAt);
  if (!created) return false;
  if (resolved.from && created < resolved.from) return false;
  if (resolved.to && created > resolved.to) return false;
  return true;
}

/** A cycle-scoped row for cycle-level reports: the cycle itself must sit inside the window. */
export function cycleInRange(cycle: RangeCycle, resolved: ResolvedRange): boolean {
  if (resolved.key === 'all') return true;
  if (resolved.key === 'cycle') return cycle.id === resolved.cycleId;
  const anchor = toDate(cycle.startedAt) ?? toDate(cycle.createdAt);
  if (!anchor) return false;
  if (resolved.from && anchor < resolved.from) return false;
  if (resolved.to && anchor > resolved.to) return false;
  return true;
}
